import { getApiBaseUrl } from "@/config/runtime";
import type {
  SowiftAnalysisCreate,
  SowiftAnalysisSessionResponse,
  SowiftContextItem,
  SowiftContextItemCreate,
  SowiftContextItemUpdate,
  SowiftDocumentResponse,
  SowiftDocumentUpdateContent,
  SowiftGenerateSOWRequest,
  SowiftInterviewMessage,
  SowiftProjectCreate,
  SowiftProjectResponse,
  SowiftProjectSummary,
  SowiftProjectUpdate,
  SowiftRefineRequest,
} from "@/types";
import { api, getHeaders } from "../api";

export const sowiftService = {
  // Projects
  listProjects: (page = 1, pageSize = 20, status?: string) => {
    const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
    if (status) params.set("status", status);
    return api.get<{
      data: SowiftProjectSummary[];
      meta: { total: number; page: number; pageSize: number; totalPages: number };
    }>(`/apps/sowift/projects?${params.toString()}`);
  },

  getProject: (uuid: string) => api.get<{ data: SowiftProjectResponse }>(`/apps/sowift/projects/${uuid}`),

  createProject: (data: SowiftProjectCreate) =>
    api.post<{ data: SowiftProjectResponse }>("/apps/sowift/projects", data),

  updateProject: (uuid: string, data: SowiftProjectUpdate) =>
    api.put<{ data: SowiftProjectResponse }>(`/apps/sowift/projects/${uuid}`, data),

  deleteProject: (uuid: string) => api.delete<{ message: string }>(`/apps/sowift/projects/${uuid}`),

  // Context Items
  listContextItems: (projectUuid: string) =>
    api.get<{ data: SowiftContextItem[] }>(`/apps/sowift/projects/${projectUuid}/context`),

  createContextItem: (projectUuid: string, data: SowiftContextItemCreate) =>
    api.post<{ data: SowiftContextItem }>(`/apps/sowift/projects/${projectUuid}/context`, data),

  updateContextItem: (projectUuid: string, itemUuid: string, data: SowiftContextItemUpdate) =>
    api.put<{ data: SowiftContextItem }>(`/apps/sowift/projects/${projectUuid}/context/${itemUuid}`, data),

  deleteContextItem: (projectUuid: string, itemUuid: string) =>
    api.delete<{ message: string }>(`/apps/sowift/projects/${projectUuid}/context/${itemUuid}`),

  retrySummary: (projectUuid: string, itemUuid: string) =>
    api.post<{ data: SowiftContextItem }>(`/apps/sowift/projects/${projectUuid}/context/${itemUuid}/retry-summary`),

  // Documents
  listDocuments: (projectUuid: string) =>
    api.get<{ data: SowiftDocumentResponse[] }>(`/apps/sowift/projects/${projectUuid}/documents`),

  getDocument: (projectUuid: string, docUuid: string) =>
    api.get<{ data: SowiftDocumentResponse }>(`/apps/sowift/projects/${projectUuid}/documents/${docUuid}`),

  updateDocument: (projectUuid: string, docUuid: string, data: SowiftDocumentUpdateContent) =>
    api.put<{ data: SowiftDocumentResponse }>(`/apps/sowift/projects/${projectUuid}/documents/${docUuid}`, data),

  generateSOW: (projectUuid: string, data: SowiftGenerateSOWRequest) =>
    api.post<{ operation_id: string }>(`/apps/sowift/projects/${projectUuid}/documents/generate/stream`, data),

  refineSection: (projectUuid: string, data: SowiftRefineRequest) =>
    api.post<{ operation_id: string }>(`/apps/sowift/projects/${projectUuid}/documents/refine/stream`, data),

  // Analysis
  listAnalysisSessions: (projectUuid: string) =>
    api.get<{ data: SowiftAnalysisSessionResponse[] }>(`/apps/sowift/projects/${projectUuid}/analysis`),

  getAnalysisSession: (projectUuid: string, sessionUuid: string) =>
    api.get<{ data: SowiftAnalysisSessionResponse }>(`/apps/sowift/projects/${projectUuid}/analysis/${sessionUuid}`),

  createAnalysisSession: (projectUuid: string, data: SowiftAnalysisCreate) =>
    api.post<{ data: SowiftAnalysisSessionResponse }>(`/apps/sowift/projects/${projectUuid}/analysis`, data),

  sendInterviewMessage: (projectUuid: string, sessionUuid: string, data: SowiftInterviewMessage) =>
    api.post<{ operation_id: string }>(
      `/apps/sowift/projects/${projectUuid}/analysis/${sessionUuid}/messages/stream`,
      data,
    ),

  // Export
  exportDocument: async (
    projectUuid: string,
    documentUuid: string,
    options: { template?: string; includeCoverPage?: boolean; includeToc?: boolean } = {},
  ): Promise<Blob> => {
    const headers = await getHeaders();
    const params = new URLSearchParams();
    params.set("template", options.template || "branded");
    if (options.includeCoverPage !== undefined) {
      params.set("include_cover_page", String(options.includeCoverPage));
    }
    if (options.includeToc !== undefined) {
      params.set("include_toc", String(options.includeToc));
    }

    const response = await fetch(
      `${getApiBaseUrl()}/apps/sowift/projects/${projectUuid}/documents/${documentUuid}/export?${params.toString()}`,
      {
        method: "POST",
        headers: {
          ...Object.fromEntries(headers.entries()),
        },
      },
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Export failed: ${errorText || response.statusText}`);
    }

    return response.blob();
  },

  // Preview (PDF)
  previewDocument: async (
    projectUuid: string,
    documentUuid: string,
    options: { template?: string; includeCoverPage?: boolean; includeToc?: boolean } = {},
  ): Promise<Blob> => {
    const headers = await getHeaders();
    const params = new URLSearchParams();
    params.set("template", options.template || "branded");
    if (options.includeCoverPage !== undefined) {
      params.set("include_cover_page", String(options.includeCoverPage));
    }
    if (options.includeToc !== undefined) {
      params.set("include_toc", String(options.includeToc));
    }

    const response = await fetch(
      `${getApiBaseUrl()}/apps/sowift/projects/${projectUuid}/documents/${documentUuid}/export/preview?${params.toString()}`,
      {
        method: "POST",
        headers: {
          ...Object.fromEntries(headers.entries()),
        },
      },
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Preview failed: ${errorText || response.statusText}`);
    }

    return response.blob();
  },
};
